<template>
    <div>
        <g-typography
            v-if="!featureFlags['live-site-responsive-design']"
            tag="h2"
            font-weight="semibold"
            class="g-mb-6"
            :value="title"
        />

        <g-media-object vertical-align="center" class="g-mb-5">
            <template #left>
                <g-typography tag="p" font-weight="semibold" :value="date" />
            </template>

            <template #right>
                <payout-status :payout="payout" :is-conversion="isConversion" :is-paid-to-agent="isPaidToAgent" />
            </template>
        </g-media-object>

        <g-base-box class="g-mb-5">
            <payout-details-list :is-payout="isPayout" :is-conversion="isConversion" :payout="payout" />

            <div v-if="isPayout">
                <g-media-object vertical-align="center" class="g-mb-3">
                    <template #left>
                        <g-typography tag="p" color="grey-6" value="Total" />
                    </template>

                    <template #right>
                        <g-typography tag="p" color="grey-6">
                            <money-display :value="total" :currency="payout.currency" />
                        </g-typography>
                    </template>
                </g-media-object>

                <g-media-object v-if="showTaxWithholding" vertical-align="center">
                    <template #left>
                        <g-typography tag="p" color="grey-6" value="IRS Tax Withholding" />
                    </template>

                    <template #right>
                        <g-typography tag="p" color="grey-6">
                            <money-display :value="taxWithholding" :currency="payout.currency" />
                        </g-typography>
                    </template>
                </g-media-object>

                <g-divider />
            </div>

            <g-media-object vertical-align="center">
                <template #left>
                    <g-typography tag="h3" font-weight="semibold" :value="totalLabel" />
                </template>

                <template #right>
                    <g-typography tag="h3" font-weight="semibold">
                        <money-display :value="totalFinal" :currency="payout.currency" />
                    </g-typography>
                </template>
            </g-media-object>
        </g-base-box>

        <g-typography tag="p">
            <a :href="FAQ_PAYOUT_SUPPORT" target="_blank">Need help?</a> Visit our FAQ for payout support.
        </g-typography>
    </div>
</template>

<script>
    import capitalize from 'lodash-es/capitalize';
    import { shortDate } from '../../../../helpers';
    import { onMounted, computed } from 'vue-demi';
    import { PAYOUT_TYPE_PAYOUT, PAYOUT_TYPE_CONVERSION } from '../../../../../app-v2/constants/payouts';
    import { FAQ_PAYOUT_SUPPORT } from '../../../../../app-v2/constants/faqs';
    import { useContactStore, useFeatureFlagsStore } from '../../../../../stores';
    import PayoutStatus from './PayoutStatus.vue';
    import PayoutDetailsList from './PayoutDetailsList.vue';
    import useEventTracking from '../../../../composables/use-event-tracking';
    import {
        EVENT_NAME_PAYOUT_DETAILS_VIEWED,
        EVENT_NAME_CONVERSION_DETAILS_VIEWED
    } from '../../../../../app-v2/constants/analytics-events';
    import MoneyDisplay from '../../../interface/elements/MoneyDisplay.vue';

    export default {
        name: 'PayoutDetails',

        components: {
            PayoutStatus,
            PayoutDetailsList,
            MoneyDisplay
        },

        props: {
            payout: {
                type: Object,
                default: null
            },
            type: {
                type: String,
                default: PAYOUT_TYPE_PAYOUT
            }
        },

        setup(props) {
            const contact = useContactStore().data;
            const featureFlags = useFeatureFlagsStore().data;

            const isPayout = computed(() => {
                return props.type === PAYOUT_TYPE_PAYOUT;
            });

            const isConversion = computed(() => {
                return props.type === PAYOUT_TYPE_CONVERSION;
            });

            const isPaidToAgent = computed(() => {
                return props.payout?.is_paid_to_agent ?? false;
            });

            const title = computed(() => {
                return capitalize(props.type) + ' Detail';
            });

            const date = computed(() => {
                return props.payout ? shortDate(props.payout.created_at) : '';
            });

            const totalLabel = computed(() => {
                return 'Total ' + (isPayout.value ? 'Payout' : 'Earned');
            });

            const events = useEventTracking();

            const total = computed(() => {
                return isPayout.value && !props.payout.is_voided ? props.payout.amount : 0;
            });

            const taxWithholding = computed(() => {
                return isPayout.value && props.payout.withholding_amount ? -props.payout.withholding_amount : 0;
            });

            const showTaxWithholding = computed(() => {
                return isPayout.value && props.payout.withholding_amount && !props.payout.is_voided;
            });

            const totalFinal = computed(() => {
                const amount = isConversion.value
                    ? props.payout.earned
                    : props.payout.amount - props.payout.withholding_amount;

                return props.payout.is_voided ? 0 : amount;
            });

            const getPaymentDrawerEventPayload = () => {
                return {
                    dl_contact_id: contact.id,
                    dl_payout_id: props.payout.id
                };
            };

            onMounted(() => {
                events.trackEvent(
                    props.type === PAYOUT_TYPE_PAYOUT
                        ? EVENT_NAME_PAYOUT_DETAILS_VIEWED
                        : EVENT_NAME_CONVERSION_DETAILS_VIEWED,
                    getPaymentDrawerEventPayload()
                );
            });

            return {
                FAQ_PAYOUT_SUPPORT,
                title,
                date,
                isConversion,
                isPayout,
                totalLabel,
                total,
                taxWithholding,
                showTaxWithholding,
                totalFinal,
                featureFlags,
                isPaidToAgent
            };
        }
    };
</script>
